import React from 'react';
import { TeacherSubject } from '../../types';

const SUBJECT_OPTIONS: { key: TeacherSubject; label: string }[] = [
  { key: 'maths', label: 'Maths' },
  { key: 'science', label: 'Science' },
  { key: 'social', label: 'Social' },
];

interface SubjectChipsSelectProps {
  /** Subjects the teacher primarily teaches. */
  value: TeacherSubject[];
  /** Subjects the teacher occasionally substitutes for. */
  substitutes?: TeacherSubject[];
  onChange: (subjects: TeacherSubject[], substitutes: TeacherSubject[]) => void;
  showSubstitutes?: boolean;
  disabled?: boolean;
}

/**
 * Two labeled rows of toggleable subject pills. A subject can live in ONE row
 * at a time — toggling it on in one row removes it from the other.
 */
const SubjectChipsSelect: React.FC<SubjectChipsSelectProps> = ({
  value,
  substitutes = [],
  onChange,
  showSubstitutes = true,
  disabled = false,
}) => {
  const toggle = (subject: TeacherSubject, row: 'primary' | 'substitute') => {
    if (row === 'primary') {
      const next = value.includes(subject)
        ? value.filter((s) => s !== subject)
        : [...value, subject];
      onChange(next, substitutes.filter((s) => !next.includes(s)));
    } else {
      const next = substitutes.includes(subject)
        ? substitutes.filter((s) => s !== subject)
        : [...substitutes, subject];
      onChange(value.filter((s) => !next.includes(s)), next);
    }
  };

  const chipClass = (selected: boolean) =>
    `rounded-full border px-4 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      selected
        ? 'border-indigo-600 bg-indigo-600 text-white'
        : 'border-slate-300 bg-white text-slate-600 hover:border-indigo-300 hover:bg-indigo-50'
    }`;

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-2 text-sm font-medium text-slate-700">Subjects I teach *</p>
        <div className="flex flex-wrap gap-2">
          {SUBJECT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              disabled={disabled}
              onClick={() => toggle(opt.key, 'primary')}
              aria-pressed={value.includes(opt.key)}
              className={chipClass(value.includes(opt.key))}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {showSubstitutes && (
        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">
            I sometimes substitute for{' '}
            <span className="font-normal text-slate-400">(optional)</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {SUBJECT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => toggle(opt.key, 'substitute')}
                aria-pressed={substitutes.includes(opt.key)}
                className={chipClass(substitutes.includes(opt.key))}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectChipsSelect;
